import React from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiLinkedin, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';

const PRODUCTS = [
  { name: 'CarerVoiz', path: '/product/voiz' },
  { name: 'CarerKMS', path: '/product/kms' },
  { name: 'Hospilot', path: '/hospilot' },
];

const WHO_WE_SERVE = [
  { name: 'Provider', path: '/verticals/provider' },
  { name: 'Payer', path: '/verticals/payer' },
  { name: 'Pharma', path: '/verticals/pharma' },
];

const LEGAL = [
  { name: 'Privacy Policy', path: '/privacy' },
  { name: 'Terms of Service', path: '/terms' },
  { name: 'HIPAA Compliance', path: '/hipaa' },
];

const Footer: React.FC = () => {
  return (
    <footer className="relative bg-gradient-to-b from-white to-blue-50/40 text-gray-600 overflow-hidden border-t border-gray-200">
      {/* Ambient glow */}
      <div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Main footer content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-10 mb-12">

          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2 mb-4 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center shadow-lg group-hover:shadow-blue-500/30 transition-shadow duration-300">
                <FiHeart className="text-white" size={20} />
              </div>
              <span className="text-xl font-bold text-gray-900">Carer Healthcare AI</span>
            </Link>

            <p className="text-sm text-gray-500 leading-relaxed mb-6 max-w-sm">
              Transforming healthcare through AI-powered unified workflows. Empowering caregivers with more information, in less time, to deliver better care.
            </p>

            {/* Social links */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/carer-healthcare-ai/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-gray-100 border border-gray-200 text-gray-600 flex items-center justify-center hover:bg-blue-500/10 hover:border-blue-500/40 hover:text-blue-600 transition-all duration-300"
                aria-label="LinkedIn"
              >
                <FiLinkedin size={16} />
              </a>
            </div>
          </div>

          {/* Products column */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Products
            </h3>
            <ul className="space-y-2.5">
              {PRODUCTS.map(product => (
                <li key={product.name}>
                  <Link
                    to={product.path}
                    className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200 inline-flex items-center gap-1 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-blue-500/0 group-hover:bg-blue-500 transition-colors duration-200" />
                    {product.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Who We Serve column */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Who We Serve
            </h3>
            <ul className="space-y-2.5">
              {WHO_WE_SERVE.map(item => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200 inline-flex items-center gap-1 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-blue-500/0 group-hover:bg-blue-500 transition-colors duration-200" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Contact
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:support@carer.ai"
                  className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200 flex items-center gap-2"
                >
                  <FiMail size={14} className="flex-shrink-0" />
                  support@carer.ai
                </a>
              </li>
              <li>
                <a
                  href="tel:+917042770098"
                  className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200 flex items-center gap-2"
                >
                  <FiPhone size={14} className="flex-shrink-0" />
                  +91 7042770098
                </a>
              </li>
              <li className="text-sm text-gray-600 flex items-start gap-2">
                <FiMapPin size={14} className="flex-shrink-0 mt-0.5" />
                <span>B2 169, BPTP Astaire garden-B, Sector 70A, Gurugram, Haryana 122101</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-8" />

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-500 text-center sm:text-left">
            © 2026 Carer Healthcare AI. All rights reserved.
          </p>

          <div className="flex items-center gap-6">
            {LEGAL.map(item => (
              <Link
                key={item.name}
                to={item.path}
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
